import { useLocation, useNavigate } from 'react-router-dom';

import { AVISO_DO_TITULAR, AVISO_SEM_ACESSO, LOCKED_FEATURE_CONFIG, type LockedFeatureKey } from '@maestra/core/constants/bloqueios';
import { useAppSelector } from '@maestra/core/store/store';

import { useCaminhoDoPro } from './caminhoDoPro';
import { FRASE_DO_MODULO } from './frasesDosModulos';
import { abrirCodigoDeAcesso } from './codigoDeAcesso';

// O MÓDULO TRAVADO, sem tela. Cópia literal de `components/LockedFeature`: a pessoa ENTRA no
// módulo e vê o que ele faz, em vez de ser expulsa para a /planos.
//  - para o DONO: o que o módulo faz (os três benefícios do núcleo) e o convite, que leva à
//    /planos. O preço não entra aqui: ele tem uma casa, que é a /planos;
//  - para o CONVIDADO de um titular sem assinatura: nada se vende (se ele assinar, continua
//    travado, porque o gate é o `owner_is_pro`). Ele lê o aviso do titular e volta ao perfil;
//  - no bloqueio do planejamento, o dono também chega daqui ao código de acesso (que o libera);
//  - a Nyta tem o voltar (ela abre em página própria no celular).

//  - para o convidado SEM ACESSO ao módulo (o titular não o liberou): o aviso de quem libera e o
//    voltar, também sem venda;
//  - para o DONO fora do plano grátis (`caminhoDoPro`, 04/10/2026): com o PIX pendente, retomar o
//    pagamento; já assinante com o perfil fora da cota, incluir o perfil; em atraso, regularizar.
//    Antes, todos ouviam "vem na assinatura" e iam aos planos, e o pendente acabava gerando outro QR.

export type PapelNoBloqueio = 'dono' | 'membro' | 'sem-acesso';

// Na web o CRM e os Shows são Vendas e Produção (o Controle de shows do menu): o título do bloqueio
// diz o nome que ela vê no menu. O núcleo segue com os nomes do app.
const TITULO_NA_WEB: Partial<Record<LockedFeatureKey, string>> = { negociacoes: 'Vendas', shows: 'Produção' };

export const useModuloTravado = (feature: LockedFeatureKey, papel: PapelNoBloqueio = 'dono') => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = LOCKED_FEATURE_CONFIG[feature];
  const artistId = /^\/artists\/([^/]+)/.exec(location.pathname)?.[1];
  const artist = useAppSelector((s) => s.artists.items.find((a) => a.id === artistId));
  const caminho = useCaminhoDoPro(artist);
  const especial = papel === 'dono' ? caminho.especial : null;
  const voltarAoPerfil = () => navigate(artistId ? `/artists/${artistId}` : '/artists');
  const ehMembro = papel !== 'dono';
  const aviso = papel === 'sem-acesso' ? AVISO_SEM_ACESSO : AVISO_DO_TITULAR;

  const modulo = TITULO_NA_WEB[feature] ?? config.title;
  return {
    feature,
    ehMembro,
    /** O que vem acima do título: o módulo e o plano; para o convidado, só o módulo (nada se vende). */
    rotulo: ehMembro ? modulo : `${modulo} · Maestra PRO`,
    // O título é a frase do que o módulo faz (13/10/2026); o convidado lê o aviso dele.
    titulo: ehMembro ? aviso.titulo : FRASE_DO_MODULO[feature],
    texto: ehMembro ? aviso.corpo : especial ? especial.texto : null,
    apoio: ehMembro ? aviso.apoio : null,
    beneficios: ehMembro ? [] : [...config.benefits],
    acao: ehMembro
      ? { rotulo: 'Voltar ao perfil', fazer: voltarAoPerfil }
      : { rotulo: especial ? especial.rotulo : config.cta.label, fazer: () => caminho.ir('/planos') },
    // O código de acesso só existe no desbloqueio, e ele libera o PLANEJAMENTO: o atalho aparece só
    // no bloqueio do planejamento (nos outros módulos, que pedem a assinatura, ele induzia ao erro).
    codigo: !ehMembro && artistId && feature === 'planning' && (!especial || caminho.situacao === 'pendente')
      ? { rotulo: 'Tenho um código de acesso', fazer: () => abrirCodigoDeAcesso(artistId) }
      : null,
    // Na Nyta, o voltar: o histórico quando há, senão o perfil.
    voltar: feature === 'nyta'
      ? () => { if (location.key !== 'default') navigate(-1); else voltarAoPerfil(); }
      : null,
  };
};

export type DadosDoModuloTravado = ReturnType<typeof useModuloTravado>;
